// Support System API Configuration
//
// Auth: the help center backend dropped the shared `x-admin-key` on 2026-07-30 and now
// wants `Authorization: Bearer <Ginox user token>` on every /api/v1/admin/* route, with
// the token's username listed in its Admin table. Every call here goes through
// supportApiFetch so the header — and the 401 handling — lives in exactly one place.

import { store } from "../../store";
import { sessionExpired } from "../../lib/sessionExpired";

// export const SUPPORT_API_BASE_URL = "https://helpstageapis.ginox.io";
export const SUPPORT_API_BASE_URL = process.env.REACT_APP_SUPPORT_API_URL;

/**
 * The signed-in admin's Ginox token.
 *
 * Support does not mint its own token — it is the same one `/api/admin/auth/login` hands
 * the Ginox and Gidex panels, so it is read straight off the shared auth slice instead of
 * being copied into localStorage. That is what lets the header's app switcher move between
 * panels without a second sign-in.
 */
export const getSupportToken = () => store.getState().auth.access_token;

// Helper function to build full API URL
export const buildApiUrl = (endpoint) => {
  // Remove leading slash if present to avoid double slashes
  const cleanEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
  return `${SUPPORT_API_BASE_URL}/${cleanEndpoint}`;
};

/**
 * Every authenticated support call goes through here.
 *
 * Returns the raw Response, exactly like fetch did, so callers keep working unchanged —
 * some read .json(), some inspect .status themselves.
 *
 * 401 is handled here because the answer is the same everywhere: the token is dead, so
 * clear it and go sign in again. Since that token is now the shared admin session, a 401
 * ends the session for every panel, not just this one. 403 is deliberately passed through
 * — that means "signed in but not an admin", which is a state the page should render, not
 * a redirect.
 */
export const supportApiFetch = async (endpoint, options = {}) => {
  const token = getSupportToken();

  const response = await fetch(buildApiUrl(endpoint), {
    ...options,
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });

  if (response.status === 401) sessionExpired();

  return response;
};

/**
 * The failure a person should read, taken from the response the backend actually sent.
 *
 * It answers in two shapes and both are deliberate: `{error: "Validation error", message:
 * "Article not found"}` labels the class in `error` and puts the reason in `message`, while
 * `{error: "Agent not found"}` carries the reason in `error` and sends no message at all.
 * Reading `message` first covers both — nothing in the backend puts the specific text in
 * `error` while leaving a vaguer one in `message`.
 *
 * The fallback is for a body that is not JSON at all: nginx answers 413 and 502 with HTML.
 */
export const readApiError = async (response, fallback) => {
  const body = await response.json().catch(() => ({}));
  const message = body.message || body.error || fallback;
  // 503 comes only from an article or document whose knowledge-base update failed. The
  // write itself was refused, so trying again is real advice rather than a platitude.
  return response.status === 503 ? `${message} Please try again.` : message;
};

// Get all agents with pagination
export const getAgents = async (page = 1, limit = 20) => {
  const response = await supportApiFetch(
    `api/v1/admin/agents?page=${page}&limit=${limit}`,
    { method: "GET" },
  );
  return response.json();
};

// Create new agent. The address has to be on a company domain; a 400 says which ones.
// 201 answers { agent, inviteUrl, expiresAt } — mail delivery cannot be confirmed from the
// server, so inviteUrl is the only copy of the link an admin can hand over. Caller reads it
// with .json(); the Response is returned unread so readApiError still owns the failure path.
export const createAgent = async (email, name) => {
  const response = await supportApiFetch("api/v1/admin/agents", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, name }),
  });
  return response;
};

// A fresh invitation link, which expires the outstanding one. Works on an active agent too,
// and that is the point: there is no self-serve password reset, so an admin re-invites and
// the agent chooses a new one. 200 answers { inviteUrl, expiresAt }.
export const resendInvite = async (id) => {
  const response = await supportApiFetch(`api/v1/admin/agents/${id}/invite`, {
    method: "POST",
  });
  return response;
};

// Delete agent
export const deleteAgent = async (id) => {
  const response = await supportApiFetch(`api/v1/admin/agents/${id}`, {
    method: "DELETE",
  });
  return response;
};

// Get agent statistics
export const getAgentStats = async (id) => {
  const response = await supportApiFetch(`api/v1/admin/agents/${id}/stats`, {
    method: "GET",
  });
  return response.json();
};

// Get all articles with pagination
export const getArticles = async (
  page = 1,
  limit = 20,
  productSection = "",
  status = "",
  category = "",
  tag = "",
) => {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("limit", limit.toString());
  if (productSection && productSection !== "all") {
    params.append("productSection", productSection);
  }
  // Omitted entirely for "all" — the backend then returns every stage, which is the
  // default. ?status=in_review is the review queue.
  if (status && status !== "all") {
    params.append("status", status);
  }
  // Both are slugs, not ids.
  if (category && category !== "all") {
    params.append("category", category);
  }
  if (tag && tag !== "all") {
    params.append("tag", tag);
  }

  const response = await supportApiFetch(
    `api/v1/admin/articles?${params.toString()}`,
    { method: "GET" },
  );
  return response.json();
};

/**
 * The five stages an article can be in, and the endpoint that moves it there.
 *
 * Any stage can follow any other — the backend enforces no path — so every action is
 * offerable at all times except the one the article is already in. Review is a workflow
 * label, not a gate.
 */
export const ARTICLE_STAGES = [
  { status: "draft", action: "revert-to-draft", label: "Draft", menuLabel: "Revert to draft" },
  { status: "in_review", action: "submit-review", label: "In review", menuLabel: "Submit for review" },
  { status: "published", action: "publish", label: "Published", menuLabel: "Publish" },
  { status: "internal_only", action: "make-internal", label: "Internal only", menuLabel: "Make internal only" },
  { status: "deprecated", action: "deprecate", label: "Deprecated", menuLabel: "Deprecate" },
];

/**
 * The two label sets an article can carry: one Category (a browsing section) and any number
 * of tags (finer topics). Structurally identical, so one pair of helpers each.
 *
 * Note the id/slug split: these are edited and deleted by **id** (a uuid), but an article
 * references them by **slug**.
 */
const jsonBody = (data) => ({
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(data),
});

// { categories: [{ id, slug, name, articleCount }] } — the count only comes back on the
// admin list, and the delete confirmation needs it.
export const getCategories = async () => {
  const response = await supportApiFetch("api/v1/admin/categories", { method: "GET" });
  return response.json();
};

export const createCategory = (data) =>
  supportApiFetch("api/v1/admin/categories", { method: "POST", ...jsonBody(data) });

export const updateCategory = (id, data) =>
  supportApiFetch(`api/v1/admin/categories/${id}`, { method: "PATCH", ...jsonBody(data) });

export const deleteCategory = (id) =>
  supportApiFetch(`api/v1/admin/categories/${id}`, { method: "DELETE" });

// { tags: [...] } — the key is `tags`, not `articleTags`.
export const getArticleTags = async () => {
  const response = await supportApiFetch("api/v1/admin/article-tags", { method: "GET" });
  return response.json();
};

export const createArticleTag = (data) =>
  supportApiFetch("api/v1/admin/article-tags", { method: "POST", ...jsonBody(data) });

export const updateArticleTag = (id, data) =>
  supportApiFetch(`api/v1/admin/article-tags/${id}`, { method: "PATCH", ...jsonBody(data) });

export const deleteArticleTag = (id) =>
  supportApiFetch(`api/v1/admin/article-tags/${id}`, { method: "DELETE" });

/**
 * Which languages each article has, plus a summary of how complete the help center is.
 * The whole set comes back at once — no paging, no filters.
 */
export const getTranslationCompleteness = async () => {
  const response = await supportApiFetch(
    "api/v1/admin/articles/translation-completeness",
    { method: "GET" },
  );
  return response.json();
};

/**
 * Reader votes on articles.
 *
 * `helpful` and `withComment` are the strings "true"/"false", not booleans — the backend
 * parses them with z.enum. Leave them out entirely to skip the filter.
 *
 * Neither this nor the ratios below carry the article's title: ArticleFeedback has no
 * relation to Article on purpose, so that a vote outlives the article it was cast on.
 * The caller joins against the article list and shows "Deleted article" for the rest.
 */
export const getArticleFeedback = async (filters = {}, page = 1, limit = 20) => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  for (const [key, value] of Object.entries(filters)) {
    if (value !== "" && value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  }

  const response = await supportApiFetch(
    `api/v1/admin/article-feedback?${params.toString()}`,
    { method: "GET" },
  );
  return response.json();
};

/**
 * Every article's split, already ordered worst-first by the backend: by how many readers
 * it failed, then by ratio. Do not re-sort — one unhelpful vote is not worse than forty
 * out of fifty.
 *
 * `ratio` is null, never 0, when nothing has been voted.
 */
export const getArticleFeedbackRatios = async () => {
  const response = await supportApiFetch("api/v1/admin/article-feedback/ratios", {
    method: "GET",
  });
  return response.json();
};

/**
 * Workflow tags label tickets and chats. Separate from article tags in every way — its own
 * endpoint, its own purpose, and these carry a colour.
 */
export const getWorkflowTags = async () => {
  const response = await supportApiFetch("api/v1/admin/tags", { method: "GET" });
  return response.json();
};

// { slug, name, color? } — color is "#rrggbb".
export const createWorkflowTag = (data) =>
  supportApiFetch("api/v1/admin/tags", { method: "POST", ...jsonBody(data) });

/**
 * Addressed by slug, not id — no read returns a tag's id. The path names the tag as it is
 * now, so sending a new slug in the body renames it.
 *
 * Send color: null to clear it; omitting the key leaves it alone.
 */
export const updateWorkflowTag = (slug, data) =>
  supportApiFetch(`api/v1/admin/tags/${slug}`, { method: "PATCH", ...jsonBody(data) });

export const deleteWorkflowTag = (slug) =>
  supportApiFetch(`api/v1/admin/tags/${slug}`, { method: "DELETE" });

/**
 * The complete set of tags the ticket or chat should end up with — a replace, not an
 * addition, so the whole list goes every time. PUT, not PATCH.
 */
export const setTicketTags = (id, tags) =>
  supportApiFetch(`api/v1/admin/tickets/${id}/tags`, {
    method: "PUT",
    ...jsonBody({ tags }),
  });

export const setChatTags = (id, tags) =>
  supportApiFetch(`api/v1/admin/chats/${id}/tags`, {
    method: "PUT",
    ...jsonBody({ tags }),
  });

/**
 * Internal notes. Staff-only — a customer never sees these. Both surfaces read and write,
 * but the length ceiling differs because each shares a column with its own reply type.
 */
export const getTicketNotes = async (id) => {
  const response = await supportApiFetch(`api/v1/admin/tickets/${id}/notes`, {
    method: "GET",
  });
  return response.json();
};

export const addTicketNote = (id, content) =>
  supportApiFetch(`api/v1/admin/tickets/${id}/notes`, {
    method: "POST",
    ...jsonBody({ content }),
  });

export const getChatNotes = async (id) => {
  const response = await supportApiFetch(`api/v1/admin/chats/${id}/notes`, {
    method: "GET",
  });
  return response.json();
};

export const addChatNote = (id, content) =>
  supportApiFetch(`api/v1/admin/chats/${id}/notes`, {
    method: "POST",
    ...jsonBody({ content }),
  });

/** { candidates: [...] } — at most 5, and only when the ticket has an email and a subject. */
export const getTicketDuplicates = async (id) => {
  const response = await supportApiFetch(`api/v1/admin/tickets/${id}/duplicates`, {
    method: "GET",
  });
  return response.json();
};

/**
 * Merge one ticket into another.
 *
 * Read the arguments carefully: `duplicateId` is the ticket that goes away, and it is the
 * one in the URL. `primaryTicketId` is the ticket that survives and collects the replies.
 */
export const mergeTicket = (duplicateId, primaryTicketId) =>
  supportApiFetch(`api/v1/admin/tickets/${duplicateId}/merge`, {
    method: "POST",
    ...jsonBody({ primaryTicketId }),
  });

/**
 * In-app alerts. One shared inbox per audience, not per person: `read` is a single column on
 * the row, so an admin marking something read marks it read for every admin.
 *
 * Its own endpoint because the bell badge polls it — asking for a page of rows every minute
 * to learn one number would be the wrong trade.
 */
export const getUnreadNotificationCount = async () => {
  const response = await supportApiFetch("api/v1/admin/notifications/unread-count", {
    method: "GET",
  });
  return response.json();
};

/** `unreadOnly` is the string "true"/"false"; leave it empty to ask for everything. */
export const getNotifications = async (unreadOnly = "", page = 1, limit = 20) => {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (unreadOnly) params.append("unreadOnly", unreadOnly);

  const response = await supportApiFetch(
    `api/v1/admin/notifications?${params.toString()}`,
    { method: "GET" },
  );
  return response.json();
};

/**
 * Marks read. Pass ids for specific rows, or nothing at all to mark everything the caller can
 * see. There is no way back — nothing un-reads a notification.
 */
export const markNotificationsRead = (ids) =>
  supportApiFetch("api/v1/admin/notifications/read", {
    method: "POST",
    ...jsonBody(ids ? { ids } : {}),
  });

/**
 * What is happening in the help center over a window: what people searched for, what they
 * searched for and did not find, what they read, and how often the bot answered.
 *
 * Reads the event tables, so it can only speak from the day event tracking shipped —
 * `window.since` comes back for exactly that reason and belongs on the page.
 *
 * Rates are null, not zero, when nothing happened. A bot nobody asked has no success rate.
 */
export const getHelpCenterAnalytics = async (days = 30, limit = 10) => {
  const response = await supportApiFetch(
    `api/v1/admin/analytics/help-center?days=${days}&limit=${limit}`,
    { method: "GET" },
  );
  return response.json();
};

export const WORKFLOW_TAG_NAME_MAX = 50;
export const TAG_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;
/** A ticket or chat can carry at most this many tags. */
export const ENTITY_TAGS_MAX = 10;
/** Notes share a column with replies, so each surface keeps that column's ceiling. */
export const NOTE_MAX = { ticket: 10000, chat: 5000 };

/** What the backend accepts in a slug, so the form can refuse before the request goes. */
export const TAXONOMY_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const TAXONOMY_SLUG_MAX = 50;
export const CATEGORY_NAME_MAX = 100;
export const ARTICLE_TAG_NAME_MAX = 50;
/** An article can carry at most this many tags. */
export const ARTICLE_TAGS_MAX = 10;

/**
 * Every privileged change the backend records. Reads are never logged.
 *
 * `to` needs an end-of-day time: the backend compares `lte <date>`, so a plain date lands
 * on midnight and hides everything that happened that day. `from` is fine as-is.
 */
export const getAuditLog = async (filters = {}, page = 1, limit = 20) => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  for (const [key, value] of Object.entries(filters)) {
    if (!value) continue;
    params.append(key, key === "to" ? `${value}T23:59:59` : value);
  }

  const response = await supportApiFetch(
    `api/v1/admin/audit-log?${params.toString()}`,
    { method: "GET" },
  );
  return response.json();
};

/** The entity kinds the log can carry, for the filter dropdown. */
export const AUDIT_ENTITY_TYPES = [
  "article",
  "document",
  "product",
  "agent",
  "admin",
  "saved_reply",
  "ticket",
  "telegram_config",
  "telegram_webhook",
  "chat",
];

/** Every action name the backend can write, grouped for the filter dropdown. */
export const AUDIT_ACTIONS = [
  "article.create", "article.update", "article.delete", "article.publish",
  "article.submit_review", "article.make_internal", "article.deprecate",
  "article.revert_draft", "article.ai_enable", "article.ai_disable", "article.restore",
  "document.create", "document.update", "document.delete",
  "document.ai_enable", "document.ai_disable",
  "product.create", "product.update", "product.delete",
  "agent.create", "agent.invite", "agent.activate", "agent.delete",
  "admin.create", "admin.delete",
  "saved_reply.create", "saved_reply.update", "saved_reply.delete",
  "ticket.assign", "ticket.status_change",
  "telegram_config.update", "telegram_config.delete",
  "telegram_webhook.register", "telegram_webhook.delete",
  "chat.claim", "chat.unassign", "chat.resolve",
];

/**
 * An article's edit history. Bodies are not included — the list is a summary, and one
 * version's full text comes from getArticleVersion.
 */
export const getArticleVersions = async (id, page = 1, limit = 20) => {
  const response = await supportApiFetch(
    `api/v1/admin/articles/${id}/versions?page=${page}&limit=${limit}`,
    { method: "GET" },
  );
  return response.json();
};

/** One version in full, translations included. */
export const getArticleVersion = async (id, versionNumber) => {
  const response = await supportApiFetch(
    `api/v1/admin/articles/${id}/versions/${versionNumber}`,
    { method: "GET" },
  );
  return response.json();
};

/**
 * Put the article back to a version. Exact, including deletions: a version with only
 * English will remove a Persian translation added since.
 *
 * `status` and `useForAI` are never touched — restoring old text cannot republish an
 * article or turn the bot back on. 409 means a dead product or a concurrent edit (the
 * message says which); 503 means nothing changed and it can be retried.
 */
export const restoreArticleVersion = async (id, versionNumber) =>
  supportApiFetch(
    `api/v1/admin/articles/${id}/versions/${versionNumber}/restore`,
    { method: "POST" },
  );

// Move an article to another stage. No body — the endpoint is the verb.
// 503 means the knowledge base could not be reached and the article did NOT move.
export const setArticleStage = async (id, action) =>
  supportApiFetch(`api/v1/admin/articles/${id}/${action}`, { method: "POST" });

// Whether the AI may use this article. Only has a visible effect while the article is
// published; on any other stage it records the intent for when it next goes live.
export const setArticleAI = async (id, useForAI) =>
  supportApiFetch(`api/v1/admin/articles/${id}/ai`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ useForAI }),
  });

// Documents have no public page, so this is their only control. Re-enabling a document
// uploaded before the text was stored returns 409 and needs the file uploading again.
export const setDocumentAI = async (id, useForAI) =>
  supportApiFetch(`api/v1/admin/documents/${id}/ai`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ useForAI }),
  });

// Create new article
export const createArticle = async (articleData) => {
  const response = await supportApiFetch("api/v1/admin/articles", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(articleData),
  });
  return response;
};

// Update article
export const updateArticle = async (id, articleData) => {
  const response = await supportApiFetch(`api/v1/admin/articles/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(articleData),
  });
  return response;
};

// Delete article
export const deleteArticle = async (id) => {
  const response = await supportApiFetch(`api/v1/admin/articles/${id}`, {
    method: "DELETE",
  });
  return response;
};

// Get admin tickets with filters
export const getAdminTickets = async ({
  page = 1,
  limit = 20,
  status = "",
  productSection = "",
  agentId = "",
  startDate = "",
  endDate = "",
  q = "",
} = {}) => {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("limit", limit.toString());
  if (status && status !== "all") {
    params.append("status", status);
  }
  // Free text across the subject, description and every reply.
  if (q && q.trim()) {
    params.append("q", q.trim());
  }
  if (productSection && productSection !== "all") {
    params.append("productSection", productSection);
  }
  if (agentId && agentId !== "all") {
    params.append("agentId", agentId);
  }
  if (startDate) {
    params.append("startDate", startDate);
  }
  if (endDate) {
    params.append("endDate", endDate);
  }

  const response = await supportApiFetch(
    `api/v1/admin/tickets?${params.toString()}`,
    { method: "GET" },
  );
  return response.json();
};

// Get ticket details by ID
export const getTicketDetails = async (ticketId) => {
  const response = await supportApiFetch(`api/v1/admin/tickets/${ticketId}`, {
    method: "GET",
  });
  return response.json();
};

// Assign ticket to agent
export const assignTicketToAgent = async (ticketId, agentId) => {
  const response = await supportApiFetch(
    `api/v1/admin/tickets/${ticketId}/assign`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ agentId }),
    },
  );
  return response;
};

// Update ticket status
export const updateTicketStatus = async (ticketId, status) => {
  const response = await supportApiFetch(
    `api/v1/admin/tickets/${ticketId}/status`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    },
  );
  return response;
};

// Get product sections (public endpoint, no auth required)
export const getProductSections = async () => {
  const url = buildApiUrl("api/v1/products");
  const response = await fetch(url, {
    method: "GET",
  });
  return response.json();
};

/**
 * Derive a product slug from its display name.
 *
 * A slug is only ever set at creation — it is never re-derived on rename, because
 * `productSection` on articles, tickets and article versions is a plain string with no
 * foreign key, so changing it orphans all of them. That makes this value permanent, which
 * is why it strips punctuation instead of only collapsing spaces: "Ginox AI (Beta)" has
 * to become `ginox_ai_beta`, not `ginox_ai_(beta)`.
 *
 * Returns "" for a name with nothing slug-able in it, so the caller can refuse it rather
 * than post an empty slug.
 */
export const slugifyProductName = (name) =>
  String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

/**
 * Flatten `getProductSections()` into the `{ id, name }` option rows the selects expect.
 *
 * That endpoint returns a tree — roots at the top level, descendants nested under
 * `children` to any depth — so reading `data` alone silently drops every sub-product,
 * which is how Ginox AI disappeared from the article and filter selects. Walking only
 * one level down has the same bug one level lower, hence the recursion. Production
 * still serves the older flat payload with no `children` key at all, hence the
 * `Array.isArray` guard rather than a plain `.map`.
 *
 * `id` is the product **slug**, not its record id: that is the value submitted as
 * `productSection`, and the callers already relied on it.
 *
 * `label` is what to render. A nested product is named with its parent in brackets
 * rather than indented: `<option>` padding is ignored by some native popups,
 * `<optgroup>` labels cannot be selected (and a root product is a valid choice), and a
 * leading space would break the browser's type-to-jump, which matches on the option
 * text from the start.
 */
export const flattenProductSections = (payload) => {
  const named = (row) => row && row.slug && row.name;

  const walk = (rows, parentName) =>
    (Array.isArray(rows) ? rows : []).filter(named).flatMap((row) => [
      {
        id: row.slug,
        name: row.name,
        parentName: parentName || null,
        label: parentName ? `${row.name} (${parentName})` : row.name,
      },
      ...walk(row.children, row.name),
    ]);

  return walk(payload?.data, null);
};

// Get all documents (Knowledge Base) with pagination
export const getDocuments = async (page = 1, limit = 20) => {
  const response = await supportApiFetch(
    `api/v1/admin/documents?page=${page}&limit=${limit}`,
    { method: "GET" },
  );
  return response.json();
};

// Create new document (multipart/form-data — no Content-Type, the browser sets the
// multipart boundary itself)
export const createDocument = async (title, file) => {
  const formData = new FormData();
  formData.append("title", title);
  formData.append("file", file);

  const response = await supportApiFetch("api/v1/admin/documents", {
    method: "POST",
    body: formData,
  });
  return response;
};

// Update document (multipart/form-data)
export const updateDocument = async (id, title, file = null) => {
  const formData = new FormData();
  if (title) {
    formData.append("title", title);
  }
  if (file) {
    formData.append("file", file);
  }

  const response = await supportApiFetch(`api/v1/admin/documents/${id}`, {
    method: "PATCH",
    body: formData,
  });
  return response;
};

// Delete document
export const deleteDocument = async (id) => {
  const response = await supportApiFetch(`api/v1/admin/documents/${id}`, {
    method: "DELETE",
  });
  return response;
};

// Get all feedbacks with pagination
export const getFeedbacks = async (page = 1, limit = 20) => {
  const response = await supportApiFetch(
    `api/v1/admin/feedback?page=${page}&limit=${limit}`,
    { method: "GET" },
  );
  return response.json();
};

// Get admin live chat sessions with filters
export const getAdminLiveChatSessions = async ({
  page = 1,
  limit = 20,
  status = "",
  product = "",
} = {}) => {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("limit", limit.toString());
  if (status && status !== "all") {
    params.append("status", status);
  }
  if (product && product !== "all") {
    params.append("product", product);
  }

  const response = await supportApiFetch(
    `api/v1/admin/chats?${params.toString()}`,
    { method: "GET" },
  );
  return response.json();
};

// Get live chat session details with messages
export const getLiveChatSessionDetails = async (sessionId) => {
  const response = await supportApiFetch(`api/v1/admin/chats/${sessionId}`, {
    method: "GET",
  });
  return response;
};

// Get live chat ratings/feedback with pagination
export const getLiveChatRatings = async ({ page = 1, limit = 20 } = {}) => {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("limit", limit.toString());

  const response = await supportApiFetch(
    `api/v1/admin/chats/ratings?${params.toString()}`,
    { method: "GET" },
  );
  return response.json();
};

// Get Telegram configuration
export const getTelegramConfig = async () => {
  const response = await supportApiFetch("api/v1/admin/telegram", {
    method: "GET",
  });
  return response.json();
};

// Update Telegram configuration
export const updateTelegramConfig = async (chatId) => {
  const response = await supportApiFetch("api/v1/admin/telegram", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ chatId }),
  });
  return response;
};

// Delete Telegram configuration
export const deleteTelegramConfig = async () => {
  const response = await supportApiFetch("api/v1/admin/telegram", {
    method: "DELETE",
  });
  return response;
};

// Get Telegram webhook status
export const getTelegramWebhookStatus = async () => {
  const response = await supportApiFetch("api/v1/admin/telegram/webhook", {
    method: "GET",
  });
  return response.json();
};

// Register Telegram webhook
export const registerTelegramWebhook = async () => {
  const response = await supportApiFetch("api/v1/admin/telegram/webhook", {
    method: "POST",
  });
  return response;
};

// Remove Telegram webhook
export const removeTelegramWebhook = async () => {
  const response = await supportApiFetch("api/v1/admin/telegram/webhook", {
    method: "DELETE",
  });
  return response;
};

// Get system statistics
export const getSystemStats = async () => {
  const response = await supportApiFetch("api/v1/admin/stats", {
    method: "GET",
  });
  return response.json();
};

// Get system health status
export const getSystemHealth = async (bypassCache = false) => {
  const response = await supportApiFetch(
    `api/v1/admin/status?refresh=${bypassCache}`,
    { method: "GET" },
  );
  return response.json();
};

/**
 * The signed-in admin's own record. 200 means admin, 403 means signed in without admin
 * access, 401 means the token itself is dead (supportApiFetch already redirected).
 */
export const getAdminMe = async () =>
  supportApiFetch("api/v1/admin/me", { method: "GET" });

/**
 * Everyone with admin access to the help center. Readable by any admin, but changing the
 * list needs the API key and so happens outside the panel.
 */
export const getAdmins = async (page = 1, limit = 50) => {
  const response = await supportApiFetch(
    `api/v1/admin/admins?page=${page}&limit=${limit}`,
    { method: "GET" },
  );
  return response.json();
};

/**
 * The shared saved replies the whole team can insert. Agents' personal replies are never
 * visible here — touching one answers 404 rather than 403, so an admin cannot even probe
 * for their existence.
 *
 * There is no pagination: the endpoint returns the full set, filtered only by language.
 */
export const getSharedSavedReplies = async (language = "") => {
  const params = new URLSearchParams();
  if (language && language !== "all") {
    params.append("language", language);
  }
  const query = params.toString();
  const response = await supportApiFetch(
    `api/v1/admin/saved-replies${query ? `?${query}` : ""}`,
    { method: "GET" },
  );
  return response.json();
};

// { name, content, language }
export const createSharedSavedReply = async (data) =>
  supportApiFetch("api/v1/admin/saved-replies", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

// Same fields, all optional.
export const updateSharedSavedReply = async (id, data) =>
  supportApiFetch(`api/v1/admin/saved-replies/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

export const deleteSharedSavedReply = async (id) =>
  supportApiFetch(`api/v1/admin/saved-replies/${id}`, { method: "DELETE" });

/**
 * Upload an image or PDF with progress tracking (multipart/form-data).
 *
 * XHR rather than fetch because this one reports upload progress, so it sets its own auth
 * header instead of going through supportApiFetch.
 *
 * Resolves to { url, filename, size, mimeType }. `filename` and `size` are not
 * recoverable from the URL — the stored file is a UUID — so a download link needs them
 * from here. The server reads the real type from the file's bytes, which means the
 * returned URL's extension may differ from the name that was uploaded: always use `url`
 * verbatim rather than rebuilding it.
 *
 * PDFs are admin-only and only with type "article".
 */
export const uploadFile = (file, type = "article", onProgress = () => {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Track upload progress
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) {
        // Map upload progress to 30-90% range (0-30% is compression)
        const percentComplete = Math.round((e.loaded / e.total) * 60) + 30;
        onProgress(percentComplete);
      }
    });

    // Handle successful upload
    xhr.addEventListener("load", () => {
      if (xhr.status === 200 || xhr.status === 201) {
        onProgress(100);
        try {
          const response = JSON.parse(xhr.responseText);
          resolve(response);
        } catch (error) {
          reject(new Error("Invalid response from server"));
        }
        return;
      }

      // Anything over ~11MB is refused by nginx before Express sees it, and nginx answers
      // with an HTML error page — so a failure body is not necessarily JSON.
      const isJson = (xhr.getResponseHeader("content-type") || "").includes(
        "application/json",
      );
      if (!isJson) {
        reject(
          new Error(
            xhr.status === 413
              ? "File is too large to upload."
              : `Upload failed with status ${xhr.status}`,
          ),
        );
        return;
      }
      try {
        const body = JSON.parse(xhr.responseText);
        reject(
          new Error(
            body.message || body.error || `Upload failed with status ${xhr.status}`,
          ),
        );
      } catch (error) {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    // Handle network errors
    xhr.addEventListener("error", () => {
      reject(new Error("Network error. Check your connection and try again."));
    });

    // Handle abort
    xhr.addEventListener("abort", () => {
      reject(new Error("Upload cancelled"));
    });

    // Prepare form data
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);

    // Send request
    xhr.open("POST", buildApiUrl("api/v1/admin/upload"));
    xhr.setRequestHeader("Authorization", `Bearer ${getSupportToken()}`);
    xhr.send(formData);

    // Store xhr instance for potential cancellation
    xhr._file = file;
  });
};

/** Kept so existing image call sites read naturally. Same endpoint, same contract. */
export const uploadImage = uploadFile;

export default {
  SUPPORT_API_BASE_URL,
  getSupportToken,
  buildApiUrl,
  supportApiFetch,
  readApiError,
  getAdminMe,
  getAdmins,
  getSharedSavedReplies,
  createSharedSavedReply,
  updateSharedSavedReply,
  deleteSharedSavedReply,
  getAgents,
  createAgent,
  resendInvite,
  deleteAgent,
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  getArticleTags,
  createArticleTag,
  updateArticleTag,
  deleteArticleTag,
  TAXONOMY_SLUG_PATTERN,
  ARTICLE_TAGS_MAX,
  getTranslationCompleteness,
  getArticleFeedback,
  getArticleFeedbackRatios,
  getWorkflowTags,
  createWorkflowTag,
  updateWorkflowTag,
  deleteWorkflowTag,
  setTicketTags,
  setChatTags,
  getUnreadNotificationCount,
  getNotifications,
  markNotificationsRead,
  getHelpCenterAnalytics,
  getTicketNotes,
  addTicketNote,
  getChatNotes,
  addChatNote,
  getTicketDuplicates,
  mergeTicket,
  ENTITY_TAGS_MAX,
  NOTE_MAX,
  getAuditLog,
  AUDIT_ENTITY_TYPES,
  AUDIT_ACTIONS,
  getArticleVersions,
  getArticleVersion,
  restoreArticleVersion,
  getArticles,
  ARTICLE_STAGES,
  createArticle,
  updateArticle,
  deleteArticle,
  setArticleStage,
  setArticleAI,
  setDocumentAI,
  getAdminTickets,
  getTicketDetails,
  assignTicketToAgent,
  updateTicketStatus,
  getProductSections,
  flattenProductSections,
  slugifyProductName,
  getDocuments,
  createDocument,
  updateDocument,
  deleteDocument,
  getFeedbacks,
  getAdminLiveChatSessions,
  getLiveChatSessionDetails,
  getLiveChatRatings,
  getTelegramConfig,
  updateTelegramConfig,
  deleteTelegramConfig,
  getTelegramWebhookStatus,
  registerTelegramWebhook,
  removeTelegramWebhook,
  getSystemStats,
  getSystemHealth,
  uploadFile,
  uploadImage,
};
